import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, BackButton, ContentSpinner, useToast } from '../../components/ui';
// Arc Canvas 模式 - 不需要 SlideRenderer

function ContentGeneratorStep3() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [projectId, setProjectId] = useState(null);
  const [savedData, setSavedData] = useState(null);
  const [cards, setCards] = useState([]);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [visualCards, setVisualCards] = useState([]);
  const [isGeneratingVisualCards, setIsGeneratingVisualCards] = useState(false);
  const [imageStyle, setImageStyle] = useState('flat'); // from step2
  const [theme, setTheme] = useState('rose'); // from step2

  // 防止 React StrictMode 重複執行
  const hasGeneratedRef = useRef(false);

  useEffect(() => {
    // 防止 React StrictMode 重複執行
    if (hasGeneratedRef.current) return;
    loadData();
  }, [navigate]);

  // 載入資料 - 優先從 URL 參數讀取 projectId
  const loadData = async () => {
    // 優先從 URL 參數讀取 projectId
    const urlProjectId = searchParams.get('projectId');
    const currentProjectId = urlProjectId || sessionStorage.getItem('currentProjectId');

    if (!currentProjectId) {
      toast.warning('找不到專案，請重新開始');
      navigate('/templates/content-generator');
      return;
    }

    // 儲存 projectId 到 state
    setProjectId(currentProjectId);

    // 嘗試從 Supabase 載入 project 資料
    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/projects/${currentProjectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();

        if (result.success && result.project) {
          const project = result.project;
          console.log('📦 從 Supabase 載入 Project:', project.id);

          // 標記已執行，防止重複
          hasGeneratedRef.current = true;

          // 設定 imageStyle 和 theme
          if (project.imageStyle) {
            setImageStyle(project.imageStyle);
          }
          if (project.theme) {
            setTheme(project.theme);
          }

          // 如果有已生成的圖片，直接載入
          if (project.generatedImages && project.generatedImages.length > 0) {
            console.log('📦 使用 Supabase 儲存的圖卡:', project.generatedImages.length);
            setVisualCards(project.generatedImages);

            // 從 generatedImages 構建 cards
            const carouselCards = project.generatedImages.map((card, index) => ({
              id: index + 1,
              pageNumber: card.page || index + 1,
              title: card.title || '',
              subtitle: card.subtitle || '',
              description: card.description || '',
              isFirstPage: index === 0
            }));
            setCards(carouselCards);

            toast.success(`載入 ${project.generatedImages.length} 張已生成的圖卡`);
            return;
          }

          // 如果有 selectedContent 但沒有 generatedImages，生成圖卡
          if (project.selectedContent && Array.isArray(project.selectedContent)) {
            const carouselCards = project.selectedContent.map((page, index) => ({
              id: index + 1,
              pageNumber: page.page || index + 1,
              title: page.title || '',
              subtitle: page.subtitle || '',
              description: page.description || '',
              isFirstPage: index === 0
            }));

            setCards(carouselCards);
            setSavedData({
              selectedCarousel: project.selectedContent,
              selectedImageStyle: project.imageStyle,
              selectedTheme: project.theme
            });

            toast.success(`Loaded ${carouselCards.length} carousel pages!`);
            generateVisualCards(project.selectedContent, project.imageStyle, project.theme);
            return;
          }
        }
      } catch (error) {
        console.error('Failed to load project from Supabase:', error);
      }
    }

    // Fallback: Get data from sessionStorage
    const data = sessionStorage.getItem('contentGeneratorData');
    if (!data) {
      toast.warning('Please complete previous steps first');
      navigate('/templates/content-generator');
      return;
    }

    const parsedData = JSON.parse(data);
    setSavedData(parsedData);

    // Get imageStyle and theme from step2
    if (parsedData.selectedImageStyle) {
      setImageStyle(parsedData.selectedImageStyle);
    }
    if (parsedData.selectedTheme) {
      setTheme(parsedData.selectedTheme);
    }

    // Check if we have carousel data from step 2
    if (!parsedData.selectedCarousel || !Array.isArray(parsedData.selectedCarousel)) {
      toast.error('Carousel data format error');
      navigate('/templates/content-generator/step2');
      return;
    }

    // Convert carousel JSON to card format
    const carouselCards = parsedData.selectedCarousel.map((page, index) => ({
      id: index + 1,
      pageNumber: page.page || index + 1,
      title: page.title || '',
      subtitle: page.subtitle || '',
      description: page.description || '',
      isFirstPage: index === 0
    }));

    setCards(carouselCards);

    // 標記已執行，防止重複
    hasGeneratedRef.current = true;

    // 檢查是否有快取的圖片（相同的 carousel + imageStyle + theme）
    const cachedData = sessionStorage.getItem('generatedVisualCards');
    if (cachedData) {
      try {
        const cached = JSON.parse(cachedData);
        const currentCarouselHash = JSON.stringify(parsedData.selectedCarousel);

        console.log('🔍 檢查快取:', {
          cachedStyle: cached.imageStyle,
          currentStyle: parsedData.selectedImageStyle,
          cachedTheme: cached.theme,
          currentTheme: parsedData.selectedTheme,
          hashMatch: cached.carouselHash === currentCarouselHash,
          hasVisualCards: cached.visualCards?.length > 0
        });

        // 檢查快取是否與當前設定匹配
        if (cached.imageStyle === parsedData.selectedImageStyle &&
            cached.theme === parsedData.selectedTheme &&
            cached.carouselHash === currentCarouselHash &&
            cached.visualCards?.length > 0) {
          console.log('📦 使用快取的圖卡');
          setVisualCards(cached.visualCards);
          toast.success(`載入 ${cached.visualCards.length} 張已生成的圖卡`);
          return;
        }
      } catch (e) {
        console.warn('快取解析失敗:', e);
      }
    }

    toast.success(`Loaded ${carouselCards.length} carousel pages!`);

    // Generate visual cards with imageStyle and theme
    generateVisualCards(parsedData.selectedCarousel, parsedData.selectedImageStyle, parsedData.selectedTheme);
  };

  const generateVisualCards = async (pages, selectedImageStyle, selectedTheme) => {
    setIsGeneratingVisualCards(true);
    // 初始化空的 visualCards array，隨後逐張填入
    setVisualCards(pages.map(p => ({ ...p, imageUrl: null, generatedByArcCanvas: true })));

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('Please login first');
        navigate('/login');
        return;
      }

      toast.info(`使用 Arc Canvas 串流生成圖卡... 風格: ${selectedImageStyle || imageStyle}, 顏色: ${selectedTheme || theme}`);

      // 使用 SSE 串流 API - 用 fetch + ReadableStream 處理 POST SSE
      const response = await fetch('/api/infocards/arc-canvas-stream', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pages,
          imageStyle: selectedImageStyle || imageStyle,
          theme: selectedTheme || theme
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // 讀取 SSE 串流
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let processedCards = pages.map(p => ({ ...p, imageUrl: null, generatedByArcCanvas: true }));

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // 解析 SSE 事件
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // 保留未完成的行

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.substring(6);
            try {
              const data = JSON.parse(dataStr);

              switch (currentEvent) {
                case 'start':
                  console.log(`🎨 [SSE] 開始生成 ${data.total} 張圖卡`);
                  break;

                case 'progress':
                  console.log(`📄 [SSE] 收到 Card ${data.index + 1}: ${data.progress}`);
                  if (data.card && data.card.success) {
                    // 更新對應位置的圖卡
                    processedCards = [...processedCards];
                    processedCards[data.index] = {
                      ...pages[data.index],
                      imageUrl: data.card.imageUrl,
                      mediaId: data.card.mediaId,
                      template: data.card.template,
                      generatedByArcCanvas: true,
                      storedInSupabase: data.card.storedInSupabase
                    };
                    setVisualCards([...processedCards]);
                    toast.success(`Card ${data.index + 1} 生成完成！`);
                  } else if (data.card && !data.card.success) {
                    toast.warning(`Card ${data.index + 1} 生成失敗`);
                  }
                  break;

                case 'complete':
                  console.log(`🎉 [SSE] 完成！成功 ${data.totalSuccess}/${data.metadata?.pageCount} 張`);

                  // 圖片元資料（不含 base64），供 Supabase 存儲
                  const visualCardsLite = processedCards.map(card => ({
                    page: card.page,
                    title: card.title,
                    subtitle: card.subtitle,
                    description: card.description,
                    mediaId: card.mediaId,
                    imageUrl: card.imageUrl,
                    template: card.template,
                    storedInSupabase: card.storedInSupabase,
                    generatedByArcCanvas: card.generatedByArcCanvas
                  }));

                  // Update project in Supabase with generated images
                  const currentProjectId = projectId || searchParams.get('projectId');
                  if (currentProjectId) {
                    try {
                      const projectToken = localStorage.getItem('token');
                      await fetch(`/api/projects/${currentProjectId}/step`, {
                        method: 'PUT',
                        headers: {
                          'Authorization': `Bearer ${projectToken}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          step: 3,
                          generatedImages: visualCardsLite,
                          generationStatus: 'completed',
                          imageStyle: selectedImageStyle || imageStyle,
                          theme: selectedTheme || theme
                        })
                      });
                      console.log('✅ Project step 3 updated with generated images');
                    } catch (error) {
                      console.error('Failed to update project:', error);
                    }
                  }

                  // Also cache locally for quick navigation
                  const cacheData = {
                    visualCards: visualCardsLite,
                    imageStyle: selectedImageStyle || imageStyle,
                    theme: selectedTheme || theme,
                    carouselHash: JSON.stringify(pages),
                    generatedAt: new Date().toISOString()
                  };
                  try {
                    sessionStorage.setItem('generatedVisualCards', JSON.stringify(cacheData));
                    console.log('💾 圖卡元資料已快取');
                  } catch (e) {
                    console.warn('快取失敗，資料過大:', e);
                  }

                  toast.success(`成功生成 ${data.totalSuccess}/${data.metadata?.pageCount} 張圖卡！`);
                  setIsGeneratingVisualCards(false);
                  break;

                case 'error':
                  console.error('❌ [SSE] 錯誤:', data.message);
                  toast.error(`生成錯誤: ${data.message}`);
                  break;
              }
            } catch (parseError) {
              console.warn('SSE data parse error:', parseError);
            }
            currentEvent = '';
          }
        }
      }

    } catch (error) {
      console.error('Visual card generation failed:', error);
      toast.error(`圖卡生成失敗: ${error.message}`);
      setIsGeneratingVisualCards(false);
    }
  };

  // 儲存當前進度並返回首頁
  const handleGoHome = async () => {
    // 儲存當前進度到 Supabase
    const currentProjectId = projectId || searchParams.get('projectId');
    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');

        // 只保存元資料，不存 base64 圖片
        const visualCardsLite = visualCards.map(card => ({
          page: card.page,
          title: card.title,
          subtitle: card.subtitle,
          description: card.description,
          mediaId: card.mediaId,
          imageUrl: card.imageUrl,
          template: card.template,
          storedInSupabase: card.storedInSupabase,
          generatedByArcCanvas: card.generatedByArcCanvas
        }));

        await fetch(`/api/projects/${currentProjectId}/step`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            step: 3,
            generatedImages: visualCardsLite,
            generationStatus: visualCards.length > 0 ? 'completed' : 'generating',
            imageStyle: imageStyle,
            theme: theme
          })
        });

        console.log('✅ Project step 3 saved to Supabase');
        toast.success('進度已儲存，可從「Your Projects」繼續編輯');
      } catch (error) {
        console.error('Failed to save project:', error);
        toast.warning('儲存失敗，但圖片已存在雲端');
      }
    }

    // 導航到首頁
    navigate('/home');
  };

  // 返回 Step 2 重新選擇風格並重新生成
  const handleRegenerate = async () => {
    const currentProjectId = projectId || searchParams.get('projectId');

    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');

        // 清除 generatedImages，讓 Step 2 重新生成
        await fetch(`/api/projects/${currentProjectId}/step`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            step: 2,
            generatedImages: null,  // 清除已生成的圖片
            generationStatus: 'pending'  // 重置狀態
          })
        });

        console.log('✅ Project reset to step 2 for regeneration');
      } catch (error) {
        console.error('Failed to reset project:', error);
      }
    }

    // 清除本地快取
    sessionStorage.removeItem('generatedVisualCards');

    toast.info('返回選擇風格頁面');
    // 使用 URL 參數導航到 Step 2
    navigate(`/templates/content-generator/step2?projectId=${currentProjectId}`);
  };

  const handlePublish = async () => {
    if (cards.length === 0) {
      toast.warning('No content to publish');
      return;
    }

    // 從 step1 獲取用戶輸入的主題/關鍵字
    const step1Data = savedData || {};

    // 只保存必要的元資料，不存 base64 圖片（避免 sessionStorage 超過 5MB 限制）
    // 圖片已經存在 Supabase，用 mediaId 引用
    const visualCardsLite = visualCards.map(card => ({
      page: card.page,
      title: card.title,
      subtitle: card.subtitle,
      description: card.description,
      mediaId: card.mediaId,           // Supabase 中的圖片 ID
      imageUrl: card.imageUrl,         // Supabase URL (不是 base64)
      template: card.template,
      storedInSupabase: card.storedInSupabase
    }));

    // 儲存當前的視覺卡片資料
    const publishData = {
      cards: cards,
      visualCards: visualCardsLite,
      imageStyle: imageStyle,
      theme: theme,
      contentType: 'carousel', // 圖+文模式
      timestamp: new Date().toISOString(),
      // 記憶用戶的選擇，供自動經營使用
      autoManagementPreset: {
        strategy: 'topic',
        contentType: 'carousel',
        imageStyle: imageStyle,
        theme: theme,
        topics: step1Data.keywords || [],
        keywords: step1Data.keywords || [],
        source: 'content-generator'
      }
    };

    try {
      sessionStorage.setItem('contentToPublish', JSON.stringify(publishData));
    } catch (e) {
      console.error('sessionStorage quota exceeded:', e);
      toast.error('資料過大，請重新生成');
      return;
    }

    // 更新 Supabase 進度為 Step 4
    const currentProjectId = projectId || searchParams.get('projectId');
    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`/api/projects/${currentProjectId}/step`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            step: 4, // 進入 Step 4
            generatedImages: visualCardsLite,
            generationStatus: 'completed'
          })
        });
        console.log('✅ Project step updated to 4 (entering Step 4)');
      } catch (error) {
        console.error('Failed to update project step:', error);
      }
    }

    // 使用 URL 參數導向發布設定頁 (Step 4)
    navigate(`/templates/content-generator/step4?projectId=${currentProjectId}`);
  };

  // Icon mapping for visual cards
  const getIconForType = (iconName) => {
    const iconMap = {
      clock: 'mdi:clock-outline',
      alert: 'mdi:alert-circle-outline',
      check: 'mdi:check-circle-outline',
      star: 'mdi:star-outline',
      chart: 'mdi:chart-line',
      lightbulb: 'mdi:lightbulb-on-outline',
      search: 'mdi:magnify',
      message: 'mdi:message-text-outline',
      file: 'mdi:file-document-outline',
      plus: 'mdi:plus-circle-outline'
    };
    return iconMap[iconName] || 'mdi:circle-outline';
  };

  // Render visual card component
  const renderVisualCard = (card, index) => {
    if (!card) return null;

    // 如果是 Arc Canvas 生成的圖片（base64），直接顯示
    if (card.generatedByArcCanvas && card.imageUrl) {
      return (
        <div className="w-full max-w-[600px] flex flex-col items-center">
          <img
            src={card.imageUrl}
            alt={card.title || `Card ${index + 1}`}
            className="w-full h-auto rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.15)]"
          />
          <div className="mt-4 py-2 px-4 bg-blue-50 rounded-lg text-[0.85rem] text-blue-500">
            模板: {card.template?.toUpperCase() || 'N/A'}
          </div>
        </div>
      );
    }

    // Cover card (first card) - Special layout with image + title overlay
    if (index === 0) {
      return (
        <div className="w-full max-w-[600px] aspect-[3/4] bg-white rounded-3xl overflow-hidden shadow-[8px_8px_0px_rgba(60,90,70,0.3)] flex flex-col relative">
          {/* Full Cover Image */}
          {card.imageUrl && (
            <img
              src={card.imageUrl}
              alt={card.title}
              className="w-full flex-1 object-cover"
            />
          )}

          {/* Text Overlay at Bottom */}
          <div className="p-8 bg-gradient-to-t from-black/80 to-black/40 absolute bottom-0 left-0 right-0">
            <h1 className="text-[2.5rem] font-black text-white mb-3 leading-tight [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">
              {card.title}
            </h1>
            {card.subtitle && (
              <p className="text-[1.3rem] text-white font-medium leading-snug [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)]">
                {card.subtitle}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Regular content cards
    return (
      <div className="w-full max-w-[600px] aspect-[3/4] bg-white rounded-3xl p-10 shadow-[8px_8px_0px_rgba(60,90,70,0.3)] flex flex-col">
        {/* Card Image */}
        {card.imageUrl && (
          <img
            src={card.imageUrl}
            alt={card.title}
            className="w-full h-[200px] rounded-2xl mb-6 object-cover"
          />
        )}

        {/* Card Title & Subtitle */}
        <h1 className="text-[1.6rem] font-black text-[#2D3A2E] mb-1.5 leading-tight">
          {card.title}
        </h1>
        {card.subtitle && (
          <p className="text-[0.95rem] text-[#5A6B5C] mb-5 font-medium">
            {card.subtitle}
          </p>
        )}

        {/* Card Content - Steps or Numbered */}
        {card.type === 'steps' ? (
          <div className="flex flex-col gap-3 flex-1 overflow-y-auto">
            {card.content && card.content.map((step, idx) => (
              <div
                key={idx}
                className="bg-[#E8F2EC] rounded-xl p-4 flex items-start gap-3 transition-transform duration-300 hover:translate-x-1"
              >
                <div className="w-10 h-10 bg-[#4A7C59] rounded-full flex items-center justify-center flex-shrink-0 shadow-[0_2px_6px_rgba(74,124,89,0.2)] text-white text-xl">
                  <Icon icon={getIconForType(step.icon)} />
                </div>
                <div className="flex-1">
                  <div className="text-base font-bold text-[#2D3A2E] mb-1">
                    {step.title}
                  </div>
                  <div
                    className="text-[0.85rem] text-[#5A6B5C] leading-snug"
                    dangerouslySetInnerHTML={{ __html: step.description }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3 flex-1 overflow-y-auto">
            {card.content && card.content.map((step, idx) => (
              <div key={idx} className="flex gap-3 items-start mb-3">
                <div className="w-[50px] h-[50px] bg-gradient-to-br from-[#E8F2EC] to-[#D8E5DD] rounded-xl flex items-center justify-center text-2xl font-black text-[#4A7C59] flex-shrink-0 shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <div className="text-base font-bold text-[#2D3A2E] mb-1">
                    {step.title}
                  </div>
                  <div
                    className="text-[0.85rem] text-[#5A6B5C] leading-snug"
                    dangerouslySetInnerHTML={{ __html: step.description }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer (if enabled) */}
        {card.stats && card.stats.enabled && (
          <div className="text-center mt-auto pt-4">
            <div className="text-[2rem] font-black text-[#4A7C59] mb-1">
              {card.stats.percentage}%
            </div>
            <div className="text-[0.9rem] font-bold text-[#2D3A2E]">
              {card.stats.label}
            </div>
            <div className="text-xs text-[#5A6B5C]">
              {card.stats.sublabel}
            </div>
          </div>
        )}

        {/* Footer text */}
        {card.footer && !card.stats?.enabled && (
          <div className="text-center p-4 bg-[#F8FBF9] rounded-lg border-2 border-dashed border-[#B5D4C1] mt-auto">
            <div className="text-[0.85rem] text-[#4A7C59] font-semibold">
              {card.footer}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (cards.length === 0) {
    return (
      <div className="relative min-h-screen bg-gray-100 flex flex-col">
        <div className="bg-white border-b border-gray-200 py-4 px-8 flex items-center justify-between sticky top-0 z-[100]">
          <BackButton onClick={() => navigate(-1)}>
            Back
          </BackButton>
        </div>
        <div className="flex flex-col items-center justify-center h-[400px]">
          <ContentSpinner text="Loading content..." />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Header - Fixed at top */}
      <div className="bg-white border-b border-gray-200 py-4 px-8 flex items-center justify-between flex-shrink-0 z-[100]">
        {/* Home Button - Left */}
        <Button
          onClick={handleGoHome}
          variant="primary"
          size="md"
          icon="mdi:home"
        >
          Home
        </Button>

        {/* Style Info - Center */}
        <div className="flex items-center gap-4 text-[0.85rem] text-slate-500">
          <span>風格: <strong>{imageStyle}</strong></span>
          <span>顏色: <strong>{theme}</strong></span>
        </div>

        <div className="w-20" /> {/* Spacer for layout balance */}
      </div>

      {/* Main Content - Sidebar + Selected Card (Scrollable) */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Thumbnails */}
        <div className="w-[140px] bg-white border-r border-gray-200 p-3 overflow-y-auto flex flex-col gap-2">
          {cards.map((card, index) => (
            <div
              key={card.id}
              onClick={() => setSelectedCardIndex(index)}
              className={`rounded-md cursor-pointer transition-all duration-200 p-2 flex flex-col items-center gap-1 min-h-0 ${
                selectedCardIndex === index
                  ? 'border-2 border-blue-500 bg-blue-50 shadow-[0_2px_8px_rgba(59,130,246,0.15)]'
                  : 'border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:translate-x-0.5'
              }`}
            >
              {/* Show thumbnail - Arc Canvas generated image */}
              {visualCards.length > 0 && visualCards[index]?.imageUrl ? (
                <img
                  src={visualCards[index].imageUrl}
                  alt={`Card ${index + 1}`}
                  className="w-full h-[80%] object-cover rounded mb-1"
                />
              ) : (
                <Icon
                  icon={card.isFirstPage ? 'mdi:book-open-page-variant' : 'mdi:file-document-outline'}
                  className={`text-2xl ${selectedCardIndex === index ? 'text-blue-500' : 'text-slate-400'}`}
                />
              )}
              <span className="text-xs font-semibold text-slate-500 text-center">
                {card.isFirstPage ? 'Cover' : `Card ${index + 1}`}
              </span>
            </div>
          ))}
        </div>

        {/* Center Content Area - Arc Canvas Visual Cards */}
        <div className="flex-1 p-8 overflow-y-auto flex justify-center items-start">
          {isGeneratingVisualCards ? (
            // Loading state while generating visual cards
            <div className="flex flex-col items-center justify-center h-[400px]">
              <ContentSpinner text="使用 Arc Canvas 生成圖卡..." />
              <p className="mt-2 mb-0 text-sm text-slate-400">
                風格: {imageStyle} | 顏色: {theme}
              </p>
            </div>
          ) : visualCards.length > 0 ? (
            // Show visual card if generated
            renderVisualCard(visualCards[selectedCardIndex], selectedCardIndex)
          ) : (
            // Fallback message if no visual cards
            <div className="flex flex-col items-center justify-center h-[400px] text-slate-400 text-center">
              <Icon icon="mdi:image-off-outline" className="text-6xl mb-4" />
              <p className="m-0 text-base font-semibold">
                圖卡生成中...
              </p>
              <p className="mt-2 mb-0 text-sm">
                請稍候
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar - Fixed at bottom */}
      <div className="bg-white border-t border-gray-200 py-3 px-8 flex items-center justify-between flex-shrink-0 z-[100]">
        {/* Left buttons */}
        <div className="flex items-center gap-3">
          <BackButton onClick={() => navigate(-1)}>
            Back
          </BackButton>
          <Button
            onClick={handleRegenerate}
            variant="ghost"
            size="sm"
            icon="mdi:refresh"
          >
            重新選擇風格
          </Button>
        </div>

        {/* Right button */}
        <Button
          onClick={handlePublish}
          variant="primary"
          size="sm"
          iconRight="mdi:arrow-right"
        >
          Next Step
        </Button>
      </div>
    </div>
  );
}

export default ContentGeneratorStep3;
